<script setup>
/**
 * CrmComposicao — "de que isto é feito", numa barra só.
 *
 * ⚠️ Nasceu em 2026-08-08 para APOSENTAR um desenho que o Gustavo reprovou sem rodeios:
 * "esses gráficos de barra mostrando porcentagem, acho muito feio, pode mudar tudo isso".
 *
 * O que havia era uma FILEIRA DE BARRAS — uma linha por categoria, cada uma com rótulo,
 * trilho cinza, preenchimento e dois números. Três defeitos, e nenhum deles é de gosto:
 *
 *   1. Responde a pergunta errada. Barras separadas fazem um RANKING ("qual é a maior"),
 *      e a pergunta destas telas é de COMPOSIÇÃO ("de que a coisa é feita"). Ranking com
 *      três itens é uma tabela ordenada — não precisa de gráfico.
 *   2. Repete a informação três vezes por linha: o comprimento da barra, o total e o
 *      percentual dizem a mesma coisa, lado a lado.
 *   3. Empate fica escondido. Duas barras de tamanho quase igual leem como diferentes até
 *      alguém comparar os números; numa barra repartida, empate é visivelmente empate.
 *
 * A barra única repartida resolve os três. Ela é UM objeto — o todo —, e cada pedaço é uma
 * parte dele. A legenda embaixo carrega o nome, o valor e o percentual em texto, que é onde
 * número se lê melhor de qualquer forma.
 *
 * Cor: paleta de gráfico, por ordem. Ver `constants/paletaGrafico.js`.
 */
import { computed } from 'vue';
import { corDaSerie } from './constants/paletaGrafico';

const props = defineProps({
  // [{ label, valor }] — na ordem em que devem ser lidos (normalmente do maior para o menor).
  itens: { type: Array, default: () => [] },
});

const total = computed(() =>
  props.itens.reduce((acc, i) => acc + (i.valor ?? 0), 0)
);

const partes = computed(() => {
  if (!total.value) return [];
  return props.itens.map((item, i) => ({
    ...item,
    cor: corDaSerie(i),
    // Arredonda só para EXIBIR. A largura usa a fração crua — arredondar as duas faria a
    // soma das larguras dar 99% ou 101% e abrir (ou estourar) um fio no fim da barra.
    percentual: Math.round(((item.valor ?? 0) / total.value) * 100),
    largura: ((item.valor ?? 0) / total.value) * 100,
  }));
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) -->
  <div v-if="partes.length" class="flex flex-col gap-4">
    <!-- A BARRA. `overflow-hidden` no pai + pedaços sem raio próprio: o arredondamento é do
         OBJETO, não de cada parte. Pedaços arredondados individualmente deixariam frestas
         entre eles e a barra deixaria de ler como uma coisa só. -->
    <div
      class="flex h-3 w-full rounded-full overflow-hidden bg-n-alpha-1"
      role="img"
      :aria-label="`Composição: ${partes.map(p => `${p.label} ${p.percentual}%`).join(', ')}`"
    >
      <div
        v-for="p in partes"
        :key="p.label"
        class="h-full transition-[width] duration-mj-revela ease-mj motion-reduce:transition-none"
        :style="{ width: `${p.largura}%`, backgroundColor: p.cor }"
        :title="`${p.label} · ${p.valor}`"
      />
    </div>

    <!-- A LEGENDA. O quadradinho repete a cor do pedaço, então o olho liga os dois sem
         contar posições. Valor e percentual em colunas de largura fixa: alinhados, eles se
         comparam na vertical sem que ninguém precise ler número por número. -->
    <div class="flex flex-col gap-2">
      <div
        v-for="p in partes"
        :key="`l-${p.label}`"
        class="flex items-center gap-2.5 text-mj-13"
      >
        <span
          class="size-2.5 rounded-md shrink-0"
          :style="{ backgroundColor: p.cor }"
          aria-hidden="true"
        />
        <span class="flex-1 truncate text-n-slate-11">{{ p.label }}</span>
        <span class="tabular-nums font-600 text-n-slate-12 shrink-0">
          {{ p.valor }}
        </span>
        <span
          class="w-10 text-right text-mj-12 tabular-nums text-n-slate-10 shrink-0"
        >
          {{ p.percentual }}%
        </span>
      </div>
    </div>
  </div>
</template>
